import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { sanitize } from '../../lib/sanitize';
import { Link } from 'react-router-dom';
import styles from './FeaturedCollections.module.css';

interface Collection {
  id: string;
  name: string;
  description: string;
  image_url: string;
  is_featured: boolean;
  sort_order: number;
  show_in_homepage: boolean;
  is_visible: boolean;
  slug?: string;
}

interface FeaturedCollectionsProps {
  limit?: number;
  /** Prikaz boksova sa slikom (default true). */
  showImage?: boolean;
  /** Koliko boksova u jednom redu; 0 = auto (responsivno popunjavanje). */
  perRow?: number;
  /** Prikaz opisa kategorije ispod naziva (default true). */
  showDescription?: boolean;
}

const FeaturedCollections: React.FC<FeaturedCollectionsProps> = ({ limit, showImage = true, perRow = 0, showDescription = true }) => {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        let query = supabase
          .from('categories')  // Promenio iz 'collections' u 'categories' jer se tu čuvaju kategorije
          .select('*')
          .eq('show_in_homepage', true)  // Filtriramo samo kategorije koje su označene za prikaz na početnoj stranici
          .eq('is_visible', true)  // Filtriramo samo vidljive kategorije
          .order('name', { ascending: true });  // Sortiranje po nazivu
        
        // Ako je limit definisan, ograničite broj rezultata
        if (limit) {
          query = query.limit(limit);
        }

        const { data, error } = await query;

        if (error) throw error;

        console.log('Fetched homepage categories:', data);
        setCollections(data || []);
      } catch (err: any) {
        console.error('Error fetching homepage categories:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCollections();
  }, [limit]);

  if (loading) return <div className={styles.loading}>Učitavanje kategorija...</div>;
  if (error) return <div className={styles.error}>Greška: {error}</div>;
  if (collections.length === 0) return null;

  // Kad je perRow > 0: tacno toliko boksova po redu (boksovi se srazmerno suzavaju).
  // Responsivno se ogranicava na manjim ekranima preko CSS varijabli po breakpointu.
  const cols = Math.max(0, Math.floor(perRow));
  const gridClass = `${styles.collectionsGrid} ${cols > 0 ? styles.collectionsGridFixed : ''}`;
  const gridStyle = cols > 0
    ? ({
        '--cols-desktop': cols,
        '--cols-tablet': Math.min(cols, 3),
        '--cols-mobile': Math.min(cols, 2),
      } as React.CSSProperties)
    : undefined;

  return (
    <div className={gridClass} style={gridStyle}>
      {collections.map((collection) => (
        <Link
          to={`/category/${collection.slug || collection.id}`}
          key={collection.id}
          className={`${styles.collectionCard} ${!showImage ? styles.noImage : ''}`}
        >
          {showImage && (
            <div className={styles.imageContainer}>
              <img
                src={collection.image_url}
                alt={collection.name}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'https://via.placeholder.com/300x200?text=Kategorija';
                }}
              />
            </div>
          )}
          <div className={styles.collectionInfo}>
            <h3>{collection.name}</h3>
            {showDescription && collection.description && (collection as any).show_description !== false && (
              <div
                className={styles.description}
                dangerouslySetInnerHTML={{ __html: sanitize(collection.description) }}
              />
            )}
          </div>
        </Link>
      ))}
    </div>
  );
};

export default FeaturedCollections; 